import { useState } from 'react';
import {
  APIComponentType,
  APIWorkspaceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { StatusType, Tag } from '@ardoq/status-ui';
import { Header2 } from '@ardoq/typography';
import { TruncatedTextPopover } from '@ardoq/popovers';
import { IconName } from '@ardoq/icons';
import { IconButton } from '@ardoq/button';
import { MarkdownWithImagePreview } from '@ardoq/image-preview';
import { type I18nLocale, t } from '@ardoq/i18n';
import { ComponentTypeEntityIcon } from './ComponentTypeEntityIcon';
import { WorkspaceManagerLinks } from './WorkspaceManagerLinks';
import type { GetWorkspaceManagerLinkProps } from './types';
import { getImagePreviewLabels } from './imagePreviewLabels';

export type SingleComponentTypeDatasourceInformationProps = {
  /**
   * One component type name can resolve to multiple types, one per workspace
   * the type features in. They share a name but may carry per-workspace
   * descriptions.
   */
  componentTypes: APIComponentType[];
  /** The workspaces this component type features in. */
  workspaces: APIWorkspaceAttributes[];
  canManageWorkspace: (workspaceId: ArdoqId) => boolean;
  getWorkspaceManagerLinkProps: GetWorkspaceManagerLinkProps;
  i18nLocale: I18nLocale;
};

export const SingleComponentTypeDatasourceInformation = ({
  componentTypes,
  workspaces,
  canManageWorkspace,
  getWorkspaceManagerLinkProps,
  i18nLocale,
}: SingleComponentTypeDatasourceInformationProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const componentType = componentTypes[0];
  const componentTypeName = componentType.name;
  // Descriptions are per-workspace, not global on a component type — show the
  // first one that has content.
  const description = componentTypes.find(({ description }) =>
    description?.trim()
  )?.description;

  return (
    <Stack gap="small">
      <FlexBox gap="small" align="center" minWidth="0">
        <ComponentTypeEntityIcon componentType={componentType} />
        <Box minWidth="0">
          <Header2>
            <TruncatedTextPopover>{componentTypeName}</TruncatedTextPopover>
          </Header2>
        </Box>
        <Tag
          statusType={StatusType.DEFAULT}
          label={t(
            i18nLocale,
            'inventory:datasourceInformation.componentTypeTag',
            'Component type'
          )}
        />
        <IconButton
          label={
            isExpanded
              ? t(
                  i18nLocale,
                  'inventory:datasourceInformation.collapseComponentTypeInfo',
                  'Collapse component type info'
                )
              : t(
                  i18nLocale,
                  'inventory:datasourceInformation.expandComponentTypeInfo',
                  'Expand component type info'
                )
          }
          iconName={isExpanded ? IconName.CHEVRON_UP : IconName.CHEVRON_DOWN}
          onClick={() => setIsExpanded(prev => !prev)}
        />
      </FlexBox>

      {isExpanded && (
        <Stack gap="small">
          {description && (
            <MarkdownWithImagePreview
              content={description}
              sanitationLevel="permissive"
              style={{ maxWidth: '65ch' }}
              {...getImagePreviewLabels(i18nLocale)}
            />
          )}

          <WorkspaceManagerLinks
            workspaces={workspaces}
            canManageWorkspace={canManageWorkspace}
            getWorkspaceManagerLinkProps={getWorkspaceManagerLinkProps}
            i18nLocale={i18nLocale}
          />
        </Stack>
      )}
    </Stack>
  );
};
